import React from 'react';
import { 
  Twitter, 
  Facebook, 
  Linkedin, 
  MessageCircle, 
  Mail,
  Share2,
} from 'lucide-react';
import { Button } from '../ui/button';

const SocialShareButtons = ({
  url,
  title,
  description,
  variant = 'default', // 'default', 'compact', 'icons-only'
}) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || '');
  const encodedDesc = encodeURIComponent(description || '');

  const shareLinks = [
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-[#1DA1F2] hover:bg-[#1a8cd8]',
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-[#4267B2] hover:bg-[#365899]',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-[#0A66C2] hover:bg-[#0855a3]',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#20bd5a]',
      url: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-gray-500 hover:bg-gray-600',
      url: `mailto:?subject=${encodedTitle}&body=${encodedDesc}%0A%0A${encodedUrl}`,
    },
  ];

  const handleShare = async (link) => {
    // Try native share first on mobile
    if (navigator.share && link.name !== 'Email') {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url,
        });
        return;
      } catch (err) {
        // Fall through to link method
      }
    }
    
    window.open(link.url, '_blank', 'width=600,height=400');
  };

  if (variant === 'icons-only') {
    return (
      <div className="flex gap-2" data-testid="social-share-buttons">
        {shareLinks.map((link) => (
          <Button
            key={link.name}
            variant="ghost"
            size="icon"
            className="w-10 h-10 rounded-full hover:bg-secondary"
            onClick={() => handleShare(link)}
            title={`Share on ${link.name}`}
          >
            <link.icon className="w-5 h-5" />
          </Button>
        ))}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="flex gap-2 flex-wrap" data-testid="social-share-buttons">
        {shareLinks.map((link) => (
          <Button
            key={link.name}
            size="sm"
            className={`${link.color} text-white`}
            onClick={() => handleShare(link)}
          >
            <link.icon className="w-4 h-4 mr-1" />
            {link.name}
          </Button>
        ))}
      </div>
    );
  }

  // Default variant
  return (
    <div className="space-y-3" data-testid="social-share-buttons">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">Share & Earn</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {shareLinks.map((link) => (
          <Button
            key={link.name}
            className={`${link.color} text-white flex-col h-auto py-3`}
            onClick={() => handleShare(link)}
          >
            <link.icon className="w-5 h-5 mb-1" />
            <span className="text-xs">{link.name}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default SocialShareButtons;
